import React, { useEffect } from 'react';
import { SlideContainer } from './container';
import { SlideContent } from './content';
import { Text } from './text';
import { Highlight } from './highlight';
import { isRecapInMobileApp, SlideProps } from '../Recap';
import RecapSlide from '../RecapSlide';
import { asset } from '../../../utils/misc';
import LogoLoading from '../../LogoLoading';
import styled from 'styled-components';
import html2canvas from 'html2canvas';

const primaryColor = '#ffffff';
const secondaryColor = '#e41c36';

const ShareButton = styled.button`
    background: #63171a;
    border: none;
    border-radius: 6px;
    bottom: 2rem;
    color: #ffffff;
    cursor: pointer;
    font-size: 1rem;
    left: 2rem;
    margin-top: 16px;
    outline: none;
    padding: 3px 16px;
    position: absolute;
    text-transform: uppercase;

    &:focus {
        outline: none;
    }
`;

export const Slide6 = ({ current = false, scale, data, soundEnabled = false }: SlideProps) => {
    const [file, setFile] = React.useState<File | null>(null);
    const [sharing, setSharing] = React.useState(false);
    const musicUrl = asset('/dist/audio/recap-music-06.mp3');
    const soundUrl = asset('/dist/audio/swoosh-06.mp3');
    const sizeFactor = 1;

    const prepareShareImage = async () => {
        let slide17 = document.getElementById('slide-17');

        if (!slide17 || !slide17.parentElement) return;

        slide17 = slide17.cloneNode(true) as HTMLElement;
        slide17.id = '';
        slide17.style.zIndex = '0';
        slide17.style.position = 'fixed';
        slide17.style.bottom = '-100vh';
        slide17.style.transform = `scale(1)`;
        slide17.style.minHeight = 'auto';
        slide17.style.aspectRatio = '9 / 16';
        slide17.classList.remove('position-relative');
        slide17.querySelectorAll('.anim').forEach((el) => {
            el.classList.remove(...Array.from(el.classList).filter(c => c.startsWith('anim')));
        });
        document.body.appendChild(slide17);

        const canvas = await html2canvas(slide17, {
            backgroundColor: null,
            useCORS: true,
            scale: 2
        });

        canvas.toBlob((blob) => {
            setSharing(false);
            slide17.remove();
            
            if (!blob) return;

            setFile(new File([blob], "cinemex-2025.png", { type: blob.type }));
        });
    }

    useEffect(() => {
        if (current && !file) {
            prepareShareImage();
        }
    }, [current]);

    const handleShare = async () => {
        if (isRecapInMobileApp()) {
            document.location = 'cinemex://com.cinemex/year-recap/share';
            return;
        }

        if (!file) return;

        navigator.share({
            files: [file],
            title: 'Cinemex | Recap 2025',
            text: 'Así fue mi 2025 en Cinemex ¡Tu también puedes revivir tu año en cinemex.com/recap-2025!'
        });
    };

    return (
        <RecapSlide animations={[
            ['line-1', 'popcorn-image'],
            ['line-2', 'line-4', 'line-6', 'line-8', 'line-10', 'line-12', 'line-14'],
            ['line-3', 'line-5', 'line-7', 'line-9', 'line-11', 'line-13', 'line-15'],
            ['yr-share']
        ]} focus={current} soundEnabled={soundEnabled} musicUrl={musicUrl} soundUrl={soundUrl} playSoundOnce={true}>
            <SlideContainer>
                <SlideContent id="slide-17" bg={secondaryColor} className="position-relative" scale={scale}>

                    <div style={{ position: 'relative', width: '100%' }}>
                        <Highlight
                            bg='#63171a'
                            color='#ffffff'
                            padding="6px 75px 5px 8px"
                            fontSize={`${42 * sizeFactor}px`}
                            className="mt-2 line-1 overflow-h anim anim-fade-in"
                            style={{ minHeight: 'auto', width: '100%', textAlign: 'center', display: 'block' }}
                        >
                            <span className="anim anim-slide-from-above">
                                Recap
                            </span>
                            {' '}
                            <span className="anim anim-slide-from-above">
                                2025
                            </span>
                        </Highlight>

                        <img src='/dist/images/year-recap/popcorn_bucket_2.png' style={{ position: 'absolute', top: '-25px', right: '10px', width: `${67 * sizeFactor}px` }} className='popcorn-image anim anim-scale-in anim-fade-in' />
                    </div>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-3 line-2">
                        <span className='anim anim-fade-in'>
                            COMPRASTE
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-3">
                        <span className='anim anim-fade-in'>
                            {data && data.tickets_bought}
                        </span>
                        {' '}
                        <span className='anim anim-fade-in'>
                            {data && data.tickets_bought === 1 ? 'BOLETO' : 'BOLETOS'}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-1 line-4">
                        <span className='anim anim-fade-in'>
                            TIEMPO EN SALAS
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-5">
                        <span className='anim anim-fade-in'>
                            {data && data.hours_spent}
                        </span>
                        {' '}
                        <span className='anim anim-fade-in'>
                            {data && data.hours_spent === 1 ? 'HORA' : 'HORAS'}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-1 line-6">
                        <span className='anim anim-fade-in'>
                            SABOREASTE
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-7">
                        <span className='anim anim-fade-in'>
                            {data && data.popcorn_amount}
                        </span>
                        {' '}
                        <span className='anim anim-fade-in'>
                            {data && data.popcorn_amount === 1 ? 'PALOMITA' : 'PALOMITAS'}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-2 line-8">
                        <span className='anim anim-fade-in'>
                            GÉNERO FAVORITO
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-9">
                        <span className='anim anim-fade-in'>
                            {data && data.fav_genre || <>&nbsp;</>}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-2 line-10">
                        <span className='anim anim-fade-in'>
                            MEJOR EXPERIENCIA
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-11">
                        <span className='anim anim-fade-in'>
                            {data && data.concept || <>&nbsp;</>}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-2 line-12">
                        <span className='anim anim-fade-in'>
                            PUNTOS ACUMULADOS/REDIMIDOS
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-13">
                        <span className='anim anim-fade-in'>
                            {data && data.points_earned}
                            /
                            {data && data.points_redeemed}
                        </span>
                    </Text>

                    <Text fontFamily="maison-neue" fontSize={`${7 * sizeFactor}px`} color={primaryColor} className="mt-2 line-14">
                        <span className='anim anim-fade-in'>
                            NIVEL ALCANZADO
                        </span>
                    </Text>
                    <Text fontFamily="fifties-cond-med" fontSize={`${46 * sizeFactor}px`} lineHeight="0.9" color={primaryColor} className="mt-1 line-15">
                        <span className='anim anim-fade-in'>
                            {data && data.loyalty_level}
                        </span>
                    </Text>
                </SlideContent>
            </SlideContainer>
            <div className='yr-share anim anim-fade-in'>
                {!!navigator.share && file && (
                    <ShareButton onClick={handleShare} >
                        Compartir
                    </ShareButton>
                )}
            </div>
            {sharing && <div style={{zIndex:9999999,position:'absolute'}}><LogoLoading /></div>}
        </RecapSlide>
    );
};